import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { uploadFiles } from 'store/slices/files';
import { styled } from '@mui/material/styles';
import {
  Typography as MuiTypography,
  Menu as MuiMenu,
  MenuItem as MuiMenuItem,
  MenuList as MuiMenuList,
  ListItemIcon,
  Button as MuiButton,
  Tooltip,
} from '@mui/material';
import {
  AddOutlined as AddOutlinedIcon,
  MapsHomeWorkOutlined as MapsHomeWorkOutlinedIcon,
  FolderOpenOutlined as FolderOpenOutlinedIcon,
  ColorLensOutlined as ColorLensOutlinedIcon,
  FormatListBulletedOutlined as FormatListBulletedOutlinedIcon,
  AdfScannerOutlined as AdfScannerOutlinedIcon,
  UploadFileOutlined as UploadFileOutlinedIcon,
} from '@mui/icons-material';
import { createQuoteSet } from 'store/slices/marketplace';
import { usePropertyDrawers } from 'hooks/usePropertyDrawers';
import { useFileDrawers } from 'hooks/useFileDrawers';
import { useMarketplaceDrawers } from 'hooks/useMarketplaceDrawers';
import { useFileUpload } from 'hooks/useFileUpload';
import { useAsyncDispatch } from 'hooks/useAsyncDispatch';

const Button = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== 'wide',
})(({ theme, wide }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  margin: '8px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '40px',
  width: wide ? '100px' : '40px',
  minHeight: '40px',
  padding: '9.5px 10px',
  borderRadius: '6px',
  '& > .MuiButton-startIcon': {
    margin: 0,
  },
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
}));

const Typography = styled(MuiTypography, {
  shouldForwardProp: (prop) => prop !== 'wide',
})(({ wide }) => ({
  flexGrow: 1,
  fontSize: '14px',
  fontWeight: '500',
  textTransform: 'capitalize',
  display: wide ? 'inline-block' : 'none',
}));

const Menu = styled(MuiMenu)(() => ({
  top: 3,
  left: 0,
}));

const MenuList = styled(MuiMenuList)(() => ({
  minWidth: '170px',
  padding: 0,
}));

const MenuItem = styled(MuiMenuItem)(() => ({
  padding: '6px 10px',
}));

export const NewButton = ({ wide }) => {
  const dispatch = useDispatch();
  const dispatchAction = useAsyncDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { id: propertyId, fileId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { openDrawerNewProperty } = usePropertyDrawers();
  const { openDrawerNewFolder } = useFileDrawers();
  const { openDrawerNewQuoteSet, openDrawerNewQuote } = useMarketplaceDrawers();
  const { handleFileChange, HiddenInputFile } = useFileUpload();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const selectedQuoteSet = useSelector((state) => state.marketplace.selectedQuoteSet);
  const currentProperty = useSelector((state) => state.properties.currentProperty);

  const sidebarFileInputRef = useRef(null);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleCreateQuote = () => {
    if (propertyId && currentProperty) {
      const { oid, name, access } = currentProperty;
      if (access && access.includes('Write')) {
        dispatchAction(createQuoteSet({ property: { id: oid, name } }), {
          onSuccess: (data) => {
            navigate(`properties/${data?.property_id}/marketplace/${data?.id}`);
            enqueueSnackbar('Quote has been added');
          },
        });
        return;
      }
      enqueueSnackbar(`You don't have permission to create quote.`, { variant: 'error' });
    } else if (selectedQuoteSet?.id) {
      openDrawerNewQuote();
    } else {
      openDrawerNewQuoteSet();
    }
    handleClose();
  };

  const handleCreateFolder = () => {
    const canCreate =
      propertyId && currentProperty ? currentProperty.access && currentProperty.access.includes('Write') : true;

    if (!canCreate) {
      enqueueSnackbar(`You don't have permission to create a folder.`, { variant: 'error' });
      return;
    }

    openDrawerNewFolder();
  };

  const handleUploadFile = () => {
    const canUpload =
      propertyId && currentProperty ? currentProperty.access && currentProperty.access.includes('Write') : true;

    if (!canUpload) {
      enqueueSnackbar(`You don't have permission to upload files.`, { variant: 'error' });
      return;
    }

    setTimeout(() => {
      if (sidebarFileInputRef.current) {
        sidebarFileInputRef.current.value = null;
        sidebarFileInputRef.current.click();
      }
    }, 0);
  };

  const handleUploadFiles = (files = []) => {
    const currentPath = window.location.pathname;

    let params = { files, parent: fileId || '', location: currentPath };

    if (propertyId && currentProperty) {
      const { oid, id } = currentProperty;
      params = {
        files,
        parent: fileId || id,
        parentFileId: oid,
        location: currentPath,
      };
    }

    dispatch(uploadFiles(params));
    enqueueSnackbar('File upload progress', {
      variant: 'upload',
      persist: true,
      navigate,
    });
  };

  const handleArtworkRedirection = () => {
    const canCreate =
      propertyId && currentProperty ? currentProperty.access && currentProperty.access.includes('Write') : true;

    if (!canCreate) {
      enqueueSnackbar(`You don't have permission to create artwork.`, { variant: 'error' });
      return;
    }

    const isPropertiesPath = location.pathname.includes('properties');
    const propertyParam = isPropertiesPath && propertyId ? `?property=${encodeURIComponent(propertyId)}` : '';
    window.open(`/artwork${propertyParam}`, '_blank', 'noopener,noreferrer');
  };

  const menuItems = [
    { id: 1, name: 'Property', icon: <MapsHomeWorkOutlinedIcon fontSize="small" />, action: openDrawerNewProperty },
    { id: 2, name: 'Folder', icon: <FolderOpenOutlinedIcon fontSize="small" />, action: handleCreateFolder },
    { id: 3, name: 'File', icon: <UploadFileOutlinedIcon fontSize="small" />, action: handleUploadFile },
    {
      id: 4,
      name: 'Artwork',
      icon: <ColorLensOutlinedIcon fontSize="small" />,
      action: handleArtworkRedirection,
    },
    {
      id: 5,
      name: 'Task',
      icon: <FormatListBulletedOutlinedIcon fontSize="small" />,
      action: () => console.log('new Task'),
    },
    { id: 6, name: 'Quote', icon: <AdfScannerOutlinedIcon fontSize="small" />, action: handleCreateQuote },
  ];

  return (
    <>
      <HiddenInputFile
        ref={sidebarFileInputRef}
        id="sidebar-input-file-id"
        name="sidebar-input-file-name"
        multiple
        onChange={(event) => {
          const files = handleFileChange({ event, multiple: true });
          handleUploadFiles(files);
        }}
      />
      <Tooltip
        title={!wide ? 'New' : ''}
        placement="right"
        arrow
      >
        <Button
          variant="contained"
          startIcon={<AddOutlinedIcon />}
          wide={wide}
          onClick={handleClick}
        >
          <Typography wide={wide}>New</Typography>
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuList>
          {menuItems.map(({ id, name, icon, action }) => (
            <MenuItem
              key={id}
              dense
              onClick={action}
            >
              <ListItemIcon sx={{ mx: 0 }}>{icon}</ListItemIcon>
              {name}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </>
  );
};
